import { useNavigate } from 'react-router-dom';
import {
  Check,
  Copy,
  LayoutDashboard,
  List,
  MessageSquare,
  QrCode,
  Send,
  Settings,
  Timer,
  Tv,
  UsersRound,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCreateGuestRoom } from '../hooks/useCreateGuestRoom';
import { useSeoMeta } from '../hooks/useSeoMeta';
import { SEO_HOME } from '../seo/pages';
import { Button } from '@/components/ui/button';
import { LandingFinalCtaSection } from '@/components/marketing/LandingFinalCtaSection';
import { LandingHeroCountdown } from '@/components/marketing/LandingHeroCountdown';
import { LandingAudienceSection } from '@/components/marketing/LandingAudienceSection';
import { LandingFaqSection } from '@/components/marketing/LandingFaqSection';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { MarketingSiteNavLinks } from '@/components/marketing/MarketingSiteNavLinks';
import './LandingPage.css';

export default function LandingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  /* Только из контекста: до первого onAuthStateChanged — «…» (кнопка с min-width без скачка). */
  const navPrimaryLabel = user
    ? 'Открыть приложение'
    : loading
      ? '…'
      : 'Войти';
  const heroActionLabel = 'Запустить в браузере';
  const { openGuestRoom, creatingGuestRoom } = useCreateGuestRoom({ disabled: loading });

  useSeoMeta(SEO_HOME);
  const handlePrimaryCta = async () => {
    if (loading || creatingGuestRoom) return;
    if (user) {
      navigate('/dashboard');
      return;
    }
    await openGuestRoom();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavPrimaryCta = () => {
    if (loading) return;
    if (user) {
      navigate('/dashboard');
      return;
    }
    navigate('/auth');
  };

  return (
    <div className="landing-page cf-page">
      <header className="cf-nav-wrap">
        <nav className="cf-nav">
          <button type="button" className="cf-brand" onClick={scrollToTop}>
            preztimer
          </button>
          <div className="cf-nav-links">
            <MarketingSiteNavLinks />
          </div>
          <div className="cf-nav-actions">
            <Button
              variant="cta"
              size="cta-sm"
              className="shrink-0"
              onClick={handleNavPrimaryCta}
              disabled={loading}
            >
              {navPrimaryLabel}
            </Button>
          </div>
        </nav>
      </header>

      <main className="cf-main">
        <section className="cf-hero">
          <div className="cf-hero-grid">
            <div className="cf-hero-copy">
              <h1>
                Контроль тайминга <span>мероприятий любого масштаба</span>
              </h1>
              <p>
                Единая система для управления выступлениями, спикерами и временем — без срывов и хаоса.
              </p>
              <div className="cf-hero-cta">
                <div className="cf-hero-cta-main">
                  <Button
                    variant="cta"
                    size="cta"
                    className="cf-landing-cta-hero"
                    onClick={handlePrimaryCta}
                    disabled={loading || creatingGuestRoom}
                  >
                    {heroActionLabel}
                  </Button>
                  <p className="cf-hero-cta-note">Без установки. Работает сразу.</p>
                </div>
              </div>
            </div>
            <div className="cf-hero-visual">
              <div className="cf-timer-card cf-timer-card--live">
                <LandingHeroCountdown />
              </div>
              <div className="cf-message-card">
                <div className="cf-message-head">
                  <MessageSquare className="cf-message-icon" size={16} aria-hidden="true" />
                  <strong>Мгновенное сообщение</strong>
                </div>
                <p>«Осталось 2 минуты. Завершайте блок вопросов.»</p>
              </div>
            </div>
          </div>
        </section>

        <LandingAudienceSection />

        <section className="cf-section cf-how-section">
          <div className="cf-section-head">
            <h2>Как это работает</h2>
          </div>

          <div className="cf-how-cards">
            <article className="cf-how-card">
              <div className="cf-how-preview" aria-hidden="true">
                <div className="cf-how-create">
                  <div className="cf-how-create-head">
                    <span />
                    <span className="cf-how-create-plus">+</span>
                  </div>
                  <div className="cf-how-create-input">Название ивента...</div>
                  <div className="cf-how-create-row">
                    <div className="cf-how-create-input">15:00</div>
                    <div className="cf-how-create-btn">СОЗДАТЬ</div>
                  </div>
                  <div className="cf-how-create-lines">
                    <span />
                    <span />
                  </div>
                </div>
              </div>
              <h3>1. Создайте комнату</h3>
              <p>
                Создайте комнату с таймерами и сообщениями за секунды. Все настройки сохраняются автоматически.
              </p>
            </article>

            <article className="cf-how-card">
              <div className="cf-how-preview" aria-hidden="true">
                <div className="cf-how-share-stage">
                  <div className="cf-how-share">
                    <div className="cf-how-qr">
                      <QrCode size={88} strokeWidth={2.2} />
                    </div>
                    <div className="cf-how-link">
                      <span>preztimer.app/room/a45b...</span>
                      <Copy size={12} />
                    </div>
                    <div className="cf-how-share-btn">Поделиться</div>
                  </div>
                  <span className="cf-how-share-badge">
                    <Send size={18} />
                  </span>
                </div>
              </div>
              <h3>2. Поделитесь ссылкой</h3>
              <p>
                Отправьте ссылку спикерам — они сразу подключатся с любого устройства.
              </p>
            </article>

            <article className="cf-how-card">
              <div className="cf-how-preview" aria-hidden="true">
                <div className="cf-how-live">
                  <div className="cf-how-live-head">
                    <div>
                      <span />
                      <span />
                    </div>
                    <strong>LIVE: Keynote</strong>
                  </div>
                  <div className="cf-how-live-stage">
                    <div>14:59</div>
                    <div className="cf-how-live-track">
                      <span />
                    </div>
                  </div>
                  <div className="cf-how-live-msg">
                    <span>Быстрое сообщение...</span>
                    <span className="cf-how-live-send">SEND</span>
                  </div>
                </div>
              </div>
              <h3>3. Управляйте эфиром</h3>
              <p>
                Контролируйте тайминг, отправляйте сообщения спикеру и управляйте программой в реальном времени.
              </p>
            </article>
          </div>
        </section>

        <section className="cf-section cf-features" id="features">
          <div className="cf-section-head">
            <h2>Возможности платформы</h2>
          </div>
          <div className="cf-bento">
            <article className="cf-card cf-bento-timer">
              <span className="cf-feature-icon" aria-hidden="true">
                <Timer size={22} />
              </span>
              <h3>Контроль времени без срывов</h3>
              <p>
                Обратный и прямой отсчет, цветовые сигналы по зонам. Спикер всегда понимает, сколько осталось времени.
              </p>
              <div className="cf-feature-timer-preview" aria-hidden="true">
                <div className="cf-feature-timer-time">04:20</div>
                <div className="cf-feature-timer-track">
                  <span className="cf-feature-timer-green" />
                  <span className="cf-feature-timer-yellow" />
                  <span className="cf-feature-timer-red" />
                </div>
              </div>
            </article>
            <article className="cf-card cf-bento-messages">
              <span className="cf-feature-icon cf-feature-icon-secondary" aria-hidden="true">
                <MessageSquare size={22} />
              </span>
              <h3>Связь со спикером</h3>
              <p>Отправляйте подсказки спикеру прямо во время выступления. Важные сообщения выделяются цветом и сразу привлекают внимание.</p>
              <div className="cf-feature-message-preview" aria-hidden="true">
                <small>НА ЭКРАНЕ СПИКЕРА:</small>
                <div>ЗАВЕРШАЙТЕ БЛОК ВОПРОСОВ</div>
              </div>
            </article>
            <article className="cf-card cf-bento-admin">
              <div className="cf-bento-admin-copy">
                <span className="cf-feature-icon" aria-hidden="true">
                  <LayoutDashboard size={22} />
                </span>
                <h3>Полный контроль мероприятия</h3>
                <p>Пауза, продление времени, переключение спикеров — управляйте программой с любого устройства.</p>
              </div>
              <div className="cf-feature-admin-image-wrap">
                <img
                  src="/landing-admin-dashboard.webp"
                  width={512}
                  height={512}
                  loading="lazy"
                  decoding="async"
                  alt="Панель управления preztimer: список таймеров и блок сообщений для вывода на экран таймера"
                />
              </div>
            </article>
            <article className="cf-card cf-bento-sync">
              <span className="cf-feature-icon cf-feature-icon-sync" aria-hidden="true">
                <UsersRound size={22} />
              </span>
              <h3>Синхронизация всей команды</h3>
              <p>Все участники видят одно и то же в реальном времени. Команда и спикеры всегда синхронизированы.</p>
              <div className="cf-sync-avatars" aria-hidden="true">
                <span className="cf-sync-avatar cf-sync-avatar-pm">PM</span>
                <span className="cf-sync-avatar cf-sync-avatar-sp">SP</span>
                <span className="cf-sync-avatar cf-sync-avatar-ad">AD</span>
              </div>
            </article>
            <article className="cf-card cf-bento-sharing">
              <div className="cf-sharing-copy">
                <h3>Разные режимы для разных ролей</h3>
                <p>Отдельные экраны для спикера, команды и управления.</p>
              </div>
              <div className="cf-share-modes" aria-hidden="true">
                <span className="cf-share-mode cf-share-mode-stage">
                  <Tv size={12} />
                  Экран таймера
                </span>
                <span className="cf-share-mode cf-share-mode-agenda">
                  <List size={12} />
                  Программа
                </span>
                <span className="cf-share-mode cf-share-mode-admin">
                  <Settings size={12} />
                  Панель управления
                </span>
              </div>
            </article>
          </div>
        </section>

        <section
          className="cf-section cf-business-value"
          id="business-value"
          aria-label="Бизнес-ценность"
        >
          <div className="cf-business-value-inner">
            <div className="cf-business-value-cols">
              <div className="cf-business-value-col">
                <ul className="cf-business-value-list">
                  <li className="cf-business-value-item">
                    <span className="cf-business-value-check" aria-hidden="true">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <div>
                      <h3 className="cf-business-value-item-title">Мероприятия по расписанию</h3>
                      <p className="cf-business-value-item-text">
                        Исключите «съезжание» графика и переплату за аренду площадки.
                      </p>
                    </div>
                  </li>
                  <li className="cf-business-value-item">
                    <span className="cf-business-value-check" aria-hidden="true">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <div>
                      <h3 className="cf-business-value-item-title">Уверенность спикеров</h3>
                      <p className="cf-business-value-item-text">
                        Спикеры чувствуют поддержку и точно знают, сколько времени у них осталось.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="cf-business-value-col">
                <ul className="cf-business-value-list">
                  <li className="cf-business-value-item">
                    <span className="cf-business-value-check" aria-hidden="true">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <div>
                      <h3 className="cf-business-value-item-title">Меньше стресса</h3>
                      <p className="cf-business-value-item-text">
                        Организаторы сохраняют спокойствие даже при самых плотных таймингах.
                      </p>
                    </div>
                  </li>
                  <li className="cf-business-value-item">
                    <span className="cf-business-value-check" aria-hidden="true">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <div>
                      <h3 className="cf-business-value-item-title">Выше качество продакшна</h3>
                      <p className="cf-business-value-item-text">
                        Слаженная работа всей команды создаёт имидж профессионального ивента.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <LandingFaqSection />

        <LandingFinalCtaSection
          onPrimaryClick={handlePrimaryCta}
          primaryDisabled={loading || creatingGuestRoom}
          primaryLabel={heroActionLabel}
          duplicateHeroCountdown
        />
      </main>

      <MarketingFooter />
    </div>
  );
}

